import type { SearchOption } from '@webapp2/shared/components/primevue/select'
import { arrayToEntityDict, type Entity, type EntityDict, type Etag, type UserSlim } from '@webapp2/shared/model'
import { capitalize, computed, toRaw, type ComputedRef } from 'vue'
import { postGetBody } from '../../services/api'
import { adminCache } from '../../services/cache'
import { etagId, type PermissionGroup } from '../../state'
import { adminState, type AdminState, type EntitySlim } from '../state'

let initialUpdateResolve: () => void

export const initialAdminUpdate = new Promise<void>(resolve => (initialUpdateResolve = resolve))

export async function setAdminStateFromCache() {
  const [courseSlims, organizationSlims, permissionGroups, playerTechniqueSlims, userSlims] = await Promise.all([
    adminCache.getByType<EntitySlim>('CourseSlim'),
    adminCache.getByType<EntitySlim>('OrganizationSlim'),
    adminCache.getByType<PermissionGroup>('PermissionGroup'),
    adminCache.getByType<EntitySlim>('PlayerTechniqueSlim'),
    adminCache.getByType<UserSlim>('UserSlim')
  ])

  adminState.courseSlims = courseSlims
  adminState.organizationSlims = organizationSlims
  adminState.permissionGroups = permissionGroups
  adminState.playerTechniqueSlims = playerTechniqueSlims
  adminState.userSlims = userSlims
}

export function updateEntities(loadAll = false) {
  return updateByTypes(Object.keys(typeToStateKey) as AdminEntityType[], loadAll)
}

async function getByTypes(types: string[], loadAll: boolean) {
  const etags = await adminCache.getByType<Etag>('Etag')
  return await postGetBody<GetDocumentsResponse<EntityDict<Entity>>>(
    'admin/documents',
    types.map(x => {
      const etag = loadAll ? undefined : etags[etagId(x)]?.timestamp
      return { type: capitalize(x), etag }
    })
  )
}

export async function updateByTypes<K extends AdminEntityType>(types: K[], loadAll = false): Promise<void> {
  const response = await getByTypes(types, loadAll)
  if (!response) {
    console.log(`Failed to update via API using cache for type: ${types.join(', ')}`)
    return
  }

  for (const type of types) {
    const stateKey = typeToStateKey[type]
    const documents = response[type] ?? []
    const docs = arrayToEntityDict(documents ?? {}) as AdminState[typeof stateKey]

    if (loadAll) {
      adminState[stateKey] = docs
      void adminCache.deleteByType(type)
      await adminCache.setItems(Object.values(toRaw(adminState[stateKey])) as { id: string }[])
    } else {
      adminState[stateKey] = { ...adminState[stateKey], ...docs }
      await adminCache.setItems(Object.values(docs) as { id: string }[])
    }

    if (documents.length > 0) {
      const id = etagId(type)
      const timestamp = documents.reduce((acc, curr) => Math.max(acc, curr.timestamp ?? 0), 0)
      await adminCache.set(id, { id, type: 'Etag', entityType: type, timestamp })
    }
  }

  initialUpdateResolve()
}

export async function ensureById<T extends Entity>(
  type: string,
  existing: EntityDict<T>,
  ids: string[],
  setState: (loaded: EntityDict<T>) => void
) {
  const existingIds = Object.keys(existing)
  const idsToLoad = [...new Set(ids)].filter(x => !existingIds.includes(x))
  if (idsToLoad.length === 0) {
    return
  }
  const request = { type, ids: idsToLoad }
  const response = await postGetBody<T[]>('admin/documents-by-id', request)
  if (!response) {
    return
  }
  const loaded = arrayToEntityDict(response)
  setState({ ...existing, ...loaded })
}

export type GetDocumentsResponse<T extends Record<string, Entity>> = {
  [K in keyof T]?: T[K][]
}

export type AdminEntityType = keyof typeof typeToStateKey

const typeToStateKey = {
  CourseSlim: 'courseSlims',
  OrganizationSlim: 'organizationSlims',
  PlayerTechniqueSlim: 'playerTechniqueSlims',
  PermissionGroup: 'permissionGroups'
} as const

function orderedEntitySlims(slims: EntityDict<EntitySlim>) {
  return Object.values(slims).sort((a, b) => a.name.localeCompare(b.name))
}

function entitySlimsToOptions(slims: ComputedRef<EntitySlim[]>) {
  return computed(() =>
    slims.value.map(
      (x): SearchOption => ({ label: x.name, value: x.id, search: (x.name ?? x.id).toLocaleLowerCase() ?? '' })
    )
  )
}

export const techniques = computed(() => orderedEntitySlims(adminState.playerTechniqueSlims))
export const techniqueOptions = entitySlimsToOptions(techniques)
export const courses = computed(() => orderedEntitySlims(adminState.courseSlims))
export const courseOptions = entitySlimsToOptions(courses)
export const organizations = computed(() => orderedEntitySlims(adminState.organizationSlims))
export const organizationOptions = entitySlimsToOptions(organizations)
